import { EditOutlined } from '@ant-design/icons';
import { Button, Card, Form, Modal, Select, Space, Tooltip } from 'antd';
import React, { useContext, useEffect, useRef, useState } from 'react';
import { useRouteMatch } from 'react-router-dom';
import ResponsiveContainer from '../../components/ResponsiveContainer';
import TableAndFilters from '../../components/TableAndFilters';
import UserContext from '../../context/user-context';
import { errorHandler, gruposSistema } from '../../utils';
import { instanceRef } from '../../utils/axios';
import { fieldTypes } from '../../utils/filter-utils';
import AseguradoraEmpresaSelect from './componentes/AseguradoraEmpresaSelect';

const { Option } = Select;

function AseguradoraEmpresaUsuario() {
  const { grupos } = useContext(UserContext);
  const [visible, setVisible] = useState(false);
  const [loading, setLoading] = useState(false);
  const [agentes, setAgentes] = useState([]);
  const [cobradores, setCobradores] = useState([]);
  const [data, setData] = useState([]);
  const [idUsuario, setIdUsuario] = useState([]);
  const [form] = Form.useForm();
  const [formAseguradora] = Form.useForm();
  const [url, setUrl] = useState();
  const tableRef = useRef();

  const getDescripcionGrupo = (grupo) => {
    const grupoUsuario =
      grupos.length > 0 && grupos.find((item) => item.id === grupo);
    if (grupo) {
      return gruposSistema[grupoUsuario.id];
    }
    return '';
  };

  const {
    params: { id: idAseguradora }
  } = useRouteMatch();

  const styles = {
    form: { textAlign: 'left' },
    card: { marginBottom: 10, textAlign: 'left' }
  };

  const columns = [
    {
      title: 'Nombre',
      dataIndex: 'nombres',
      filterType: fieldTypes.STRING,
      sortable: true
    },
    {
      title: 'Usuario',
      dataIndex: 'usuario',
      filterType: fieldTypes.STRING,
      sortable: true
    },
    {
      title: 'Correo',
      dataIndex: 'correo',
      filterType: fieldTypes.STRING,
      sortable: true
    },
    {
      title: 'Rol',
      dataIndex: 'grupo',
      filterType: fieldTypes.ENUM,
      filters: Object.keys(gruposSistema).map((key) => ({
        text: gruposSistema[key],
        value: key
      })),
      sortable: true,
      render: (grupo) => getDescripcionGrupo(grupo)
    },
    {
      title: 'Código Agente',
      dataIndex: 'sebaot_codigo_agente',
      key: 'sebaot_codigo_agente',
      align: 'center',
      render: (_, record) => {
        return record.aseguradoraUsuario?.[0]?.sebaot_codigo_agente || '';
      }
    },
    {
      title: 'Código Cobrador',
      dataIndex: 'sebaot_codigo_cobrador',
      key: 'sebaot_codigo_cobrador',
      align: 'center',
      render: (_, record) => {
        return record.aseguradoraUsuario?.[0]?.sebaot_codigo_cobrador || '';
      }
    },
    {
      title: 'Acciones',
      key: 'action',
      align: 'left',
      width: '10px',
      render: (_, record) => (
        <Space size="small">
          <Tooltip title="Editar">
            <Button
              type="text"
              icon={<EditOutlined />}
              size="middle"
              onClick={() => updateProducto(record)}
            />
          </Tooltip>
        </Space>
      )
    }
  ];

  const handleOk = async () => {
    setLoading(true);
    try {
      if (data) {
        await instanceRef.instance.put(`aseguradorausuario/${data.id}`, {
          ...data,
          ...form.getFieldsValue()
        });
      } else {
        await instanceRef.instance.post(`aseguradorausuario`, {
          id_aseguradora: idAseguradora,
          id_usuario: idUsuario,
          ...form.getFieldsValue()
        });
      }
      tableRef.current.reload();
      setVisible(false);
      form.resetFields();
      setData(null);
      setIdUsuario(null);
    } catch (err) {
      errorHandler(err);
    }

    setLoading(false);
  };

  const handleCancel = () => {
    setVisible(false);
    form.resetFields();
  };

  function getAseguradoraEmpresaId(value) {
    if (value) {
      setUrl(`/aseguradorausuario/aseguradoraempresa/${value.key}`);
    } else {
      setUrl();
    }
  }

  const updateProducto = async (record) => {
    setIdUsuario(record.id);
    setData(record.aseguradoraUsuario[0]);
    setVisible(true);
    form.setFieldsValue({ ...record.aseguradoraUsuario[0] });
  };

  useEffect(() => {
    if (url) {
      tableRef.current.reload();
    } else {
      tableRef.current.clear();
    }
  }, [url]);

  useEffect(() => {
    if (idAseguradora) {
      const load = async () => {
        const agentesCobradores = await instanceRef.instance.get(
          `sebaot/consultar-agentes-cobradores?idAseguradora=${idAseguradora}`
        );

        if (agentesCobradores.data.data) {
          setAgentes(agentesCobradores.data.data.agentes);
          setCobradores(agentesCobradores.data.data.cobradores);
        }
      };
      load();
    }
  }, [idAseguradora]);

  return (
    <ResponsiveContainer size="large">
      <Card
        title="Gestión de usuarios de las empresas"
        style={{ textAlign: 'left' }}
      >
        <Form form={formAseguradora} layout="vertical" style={styles.form}>
          <Form.Item label="Empresa" name="empresa">
            <AseguradoraEmpresaSelect
              aseguradora={idAseguradora}
              getAseguradoraEmpresaId={getAseguradoraEmpresaId}
            />
          </Form.Item>
        </Form>

        <br />
        <TableAndFilters
          ref={tableRef}
          url={url}
          title="Usuarios de la empresa"
          columns={columns}
          showTable
          withGeneralFilter
        />
      </Card>
      <Modal
        title="Modificar configuraciones del usuario"
        open={visible}
        onOk={handleOk}
        onCancel={handleCancel}
        okButtonProps={{ loading }}
        width={1000}
      >
        <Form
          form={form}
          layout="vertical"
          name="usuarioForm"
          style={styles.form}
        >
          <Form.Item
            label="Código de agente"
            rules={[
              {
                whitespace: true
              }
            ]}
            name="sebaot_codigo_agente"
          >
            <Select
              showSearch
              allowClear
              filterOption={(input, option) =>
                option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
              }
              style={{ width: '100%' }}
              optionFilterProp="children"
              placeholder="Seleccionar Agente"
            >
              {agentes.map((item) => (
                <Option key={`${item.codigo}`} value={`${item.codigo}`}>
                  {`${item.codigo} - ${item.nombre} - Matrícula (${item.matricula})`}
                </Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item
            label="Código de cobrador"
            rules={[
              {
                whitespace: true
              }
            ]}
            name="sebaot_codigo_cobrador"
          >
            <Select
              showSearch
              allowClear
              filterOption={(input, option) =>
                option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
              }
              style={{ width: '100%' }}
              optionFilterProp="children"
              placeholder="Seleccionar Cobrador"
            >
              {cobradores.map((item) => (
                <Option key={`${item.codigo}`} value={`${item.codigo}`}>
                  {`${item.codigo} - ${item.nombre}`}
                </Option>
              ))}
            </Select>
          </Form.Item>
        </Form>
      </Modal>
    </ResponsiveContainer>
  );
}
export default AseguradoraEmpresaUsuario;
